import {
   Component,
   EventEmitter,
   Input,
   OnChanges,
   Output,
   SimpleChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CalendarModule } from 'primeng/calendar';
import { SelectButtonModule } from 'primeng/selectbutton';
import { DateTime } from 'luxon';
import {
   DashboardDateRange,
   DashboardPeriodPreset
} from '../../../domain/dashboard/dashboard-period';

type PeriodOption = {
   label: string;
   value: DashboardPeriodPreset;
};

@Component({
   selector: 'app-dashboard-period-selector',
   standalone: true,
   imports: [CommonModule, FormsModule, SelectButtonModule, CalendarModule],
   templateUrl: './dashboard-period-selector.component.html',
   styleUrls: ['./dashboard-period-selector.component.scss']
})
export class DashboardPeriodSelectorComponent implements OnChanges {
   @Input() propertyTimezone: string | null = null;
   @Input() propertyId: number | null = null;

   @Output() periodChanged = new EventEmitter<DashboardDateRange>();

   selectedPreset: DashboardPeriodPreset = 'yesterday';
   customRange: Date[] | null = null;

   customMinDate: Date | null = null;
   customMaxDate: Date | null = null;

   readonly periodOptions: PeriodOption[] = [
      { label: 'Yesterday', value: 'yesterday' },
      { label: 'Last 7 Days', value: 'last7' },
      { label: 'Custom', value: 'custom' }
   ];

   ngOnChanges(changes: SimpleChanges): void {
      if (
         (changes['propertyId'] || changes['propertyTimezone']) &&
         this.propertyId &&
         this.propertyTimezone
      ) {
         this.selectedPreset = 'yesterday';
         this.customRange = null;
         this.resetCustomDateLimits();
         this.emitPresetRange();
      }
   }

   onPresetChange(): void {
      this.customRange = null;
      this.resetCustomDateLimits();

      if (this.selectedPreset !== 'custom') {
         this.emitPresetRange();
      }
   }

   onCustomDateSelect(): void {
      if (!this.customRange?.length) {
         return;
      }

      const start = this.customRange[0];

      if (start && !this.customRange[1]) {
         this.customMinDate = start;

         const maximumEnd = this.addDays(start, 30);
         const yesterday = this.getYesterdayCalendarDate();

         this.customMaxDate =
            maximumEnd.getTime() < yesterday.getTime() ? maximumEnd : yesterday;

         return;
      }

      const end = this.customRange[1];

      if (!start || !end) {
         return;
      }

      this.periodChanged.emit({
         preset: 'custom',
         startDate: this.toDateOnly(start),
         endDate: this.toDateOnly(end)
      });
   }

   onCustomRangeClear(): void {
      this.customRange = null;
      this.resetCustomDateLimits();
   }

   private emitPresetRange(): void {
      if (!this.propertyTimezone) {
         return;
      }

      const yesterday = DateTime.now()
         .setZone(this.propertyTimezone)
         .minus({ days: 1 })
         .startOf('day');

      if (this.selectedPreset === 'last7') {
         this.periodChanged.emit({
            preset: 'last7',
            startDate: yesterday.minus({ days: 6 }).toFormat('yyyy-MM-dd'),
            endDate: yesterday.toFormat('yyyy-MM-dd')
         });

         return;
      }

      if (this.selectedPreset === 'yesterday') {
         const date = yesterday.toFormat('yyyy-MM-dd');

         this.periodChanged.emit({
            preset: 'yesterday',
            startDate: date,
            endDate: date
         });
      }
   }

   private resetCustomDateLimits(): void {
      this.customMinDate = null;

      if (this.propertyTimezone) {
         this.customMaxDate = this.getYesterdayCalendarDate();
      }
   }

   private getYesterdayCalendarDate(): Date {
      const yesterday = DateTime.now()
         .setZone(this.propertyTimezone!)
         .minus({ days: 1 });

      return new Date(yesterday.year, yesterday.month - 1, yesterday.day);
   }

   private addDays(date: Date, days: number): Date {
      const result = new Date(date);
      result.setDate(result.getDate() + days);
      return result;
   }

   private toDateOnly(date: Date): string {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
   }
}
